import { useState } from 'react'
import { Box, Typography, Chip, IconButton, Tooltip, alpha, useTheme } from '@mui/material'
import {
  FolderOpen,
  Shield,
  Settings,
  Server,
  Cloud,
  HardDrive,
  Laptop,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  Rocket,
  Info,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import CommandPreview from '../CommandPreview'
import BackupFlowPreview from './BackupFlowPreview'
import {
  ReviewAttrRow,
  ReviewCodePill,
  ReviewKicker,
  ReviewSectionCard,
  ReviewSectionGrid,
} from './WizardReviewComponents'

interface SSHConnection {
  id: number
  host: string
  username: string
  port: number
  ssh_key_id: number
  default_path?: string
  ssh_path_prefix?: string
  use_sudo?: boolean
}

interface AgentMachine {
  id: number
  name: string
  hostname?: string | null
  status: string
}

export interface WizardReviewData {
  name: string
  borgVersion?: 1 | 2
  repositoryMode: 'full' | 'observe'
  repositoryLocation: 'local' | 'ssh' | 'rclone'
  executionTarget?: 'local' | 'agent'
  agentMachineId?: number | ''
  path: string
  repoSshConnectionId: number | ''
  dataSource: 'local' | 'remote'
  sourceSshConnectionId: number | ''
  sourceDirs: string[]
  encryption: string
  passphrase: string
  compression: string
  excludePatterns: string[]
  customFlags: string
  remotePath: string
  cloudMirrorEnabled?: boolean
  rcloneRemoteName?: string
  rcloneRemotePath?: string
  rcloneSyncPolicy?: 'after_success' | 'manual' | 'scheduled'
  rcloneSyncCronExpression?: string
  rcloneSyncTimezone?: string
}

interface WizardStepReviewProps {
  mode: 'create' | 'edit' | 'import'
  data: WizardReviewData
  sshConnections: SSHConnection[]
  agentMachines?: AgentMachine[]
}

function getEncryptionLabelKey(encryption: string) {
  if (encryption === 'none') return 'wizard.review.encryptionNone'
  if (encryption.startsWith('repokey')) return 'wizard.review.encryptionRepokey'
  if (encryption.startsWith('keyfile')) return 'wizard.review.encryptionKeyfile'
  return 'wizard.review.encryptionNone'
}

export default function WizardStepReview({
  mode,
  data,
  sshConnections,
  agentMachines = [],
}: WizardStepReviewProps) {
  const { t } = useTranslation()
  const [showPassphrase, setShowPassphrase] = useState(false)
  const executionTarget = data.executionTarget ?? 'local'
  const getRcloneSyncPolicyLabel = () => {
    switch (data.rcloneSyncPolicy || 'after_success') {
      case 'manual':
        return t('wizard.location.rcloneSyncManual')
      case 'scheduled':
        return t('wizard.location.rcloneSyncScheduled')
      case 'after_success':
      default:
        return t('wizard.location.rcloneSyncAfterSuccess')
    }
  }

  const getSourceSshConnection = () => {
    if (data.dataSource !== 'remote' || !data.sourceSshConnectionId) return null
    const conn = sshConnections.find((c) => c.id === data.sourceSshConnectionId)
    if (!conn) return null
    return {
      username: conn.username,
      host: conn.host,
      port: conn.port,
      defaultPath: conn.default_path,
    }
  }

  const getRepoSshConnection = () => {
    if (data.repositoryLocation !== 'ssh' || !data.repoSshConnectionId) return null
    const conn = sshConnections.find((c) => c.id === data.repoSshConnectionId)
    return conn || null
  }

  const getSourceSshConnectionForFlow = () => {
    if (data.dataSource !== 'remote' || !data.sourceSshConnectionId) return null
    const conn = sshConnections.find((c) => c.id === data.sourceSshConnectionId)
    return conn || null
  }

  const getRepoConnectionDetails = () => {
    if (data.repositoryLocation === 'ssh' && data.repoSshConnectionId) {
      const conn = sshConnections.find((c) => c.id === data.repoSshConnectionId)
      if (conn) return { host: conn.host, username: conn.username, port: conn.port }
    }
    return { host: '', username: '', port: 22 }
  }

  const theme = useTheme()
  const repoDetails = getRepoConnectionDetails()
  const selectedAgent =
    executionTarget === 'agent' && data.agentMachineId
      ? agentMachines.find((agent) => agent.id === data.agentMachineId)
      : null
  const isEncrypted = data.encryption !== 'none'
  const hasBackupSource = data.repositoryMode === 'full' && data.sourceDirs.length > 0
  const hasPlanOwnedAgentSources =
    data.repositoryMode === 'full' && executionTarget === 'agent' && data.sourceDirs.length === 0
  const isDirectRclone = data.repositoryLocation === 'rclone' && !data.cloudMirrorEnabled
  const hasBackupConfiguration =
    data.repositoryMode === 'full' &&
    (hasBackupSource || data.excludePatterns.length > 0 || Boolean(data.customFlags))
  const cloudMirrorTarget = data.rcloneRemoteName
    ? `${data.rcloneRemoteName}:${data.rcloneRemotePath || ''}`
    : data.rcloneRemotePath || t('wizard.review.notSet')

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Backup Flow Preview */}
      {!isDirectRclone && hasBackupSource && (
        <BackupFlowPreview
          repositoryLocation={data.repositoryLocation}
          dataSource={data.dataSource}
          repositoryPath={data.path}
          sourceDirs={data.sourceDirs}
          repoSshConnection={getRepoSshConnection()}
          sourceSshConnection={getSourceSshConnectionForFlow()}
        />
      )}

      {/* Command Preview */}
      {!isDirectRclone &&
        (data.dataSource === 'local' || data.dataSource === 'remote') &&
        hasBackupSource && (
          <CommandPreview
            mode={mode === 'create' ? 'create' : 'import'}
            borgVersion={data.borgVersion}
            repositoryPath={data.path}
            repositoryLocation={data.repositoryLocation}
            host={repoDetails.host}
            username={repoDetails.username}
            port={repoDetails.port}
            encryption={data.encryption}
            compression={data.compression}
            excludePatterns={data.excludePatterns}
            sourceDirs={data.sourceDirs}
            customFlags={data.customFlags}
            remotePath={data.remotePath}
            useSudo={getRepoSshConnection()?.use_sudo}
            repositoryMode={data.repositoryMode}
            dataSource={data.dataSource}
            sourceSshConnection={getSourceSshConnection()}
          />
        )}

      {/* Manifest header + status chip */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <ReviewKicker>{t('wizard.review.configurationSummary')}</ReviewKicker>

        {mode === 'create' && data.repositoryMode === 'full' && (
          <Tooltip title={t('wizard.review.repositoryInitialized')} placement="top" arrow>
            <Chip
              icon={<Rocket size={11} />}
              label={t('wizard.review.readyToInitialize')}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                fontWeight: 600,
                bgcolor: alpha(theme.palette.success.main, 0.1),
                color: theme.palette.success.main,
                border: `1px solid ${alpha(theme.palette.success.main, 0.25)}`,
                cursor: 'help',
                '& .MuiChip-icon': { color: theme.palette.success.main, ml: '6px' },
                '& .MuiChip-label': { px: '8px' },
              }}
            />
          </Tooltip>
        )}
      </Box>

      {mode === 'create' && data.repositoryMode === 'full' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Info size={14} style={{ opacity: 0.45, flexShrink: 0 }} />
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {t('wizard.review.repositoryInitialized')}
          </Typography>
        </Box>
      )}

      {/* 2x2 section card grid */}
      <ReviewSectionGrid>
        {/* REPOSITORY */}
        <ReviewSectionCard
          icon={<FolderOpen size={14} />}
          label={t('wizard.review.repository')}
          accentColor={theme.palette.primary.main}
        >
          <ReviewAttrRow label={t('wizard.review.name')}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                fontSize: '0.8rem',
              }}
            >
              {data.name}
            </Typography>
          </ReviewAttrRow>

          <ReviewAttrRow label={t('wizard.review.mode')}>
            <Chip
              label={
                data.repositoryMode === 'full'
                  ? t('wizard.review.full')
                  : t('wizard.review.observeOnly')
              }
              size="small"
              color={data.repositoryMode === 'full' ? 'primary' : 'default'}
              sx={{ height: 17, fontSize: '0.62rem', fontWeight: 600 }}
            />
          </ReviewAttrRow>

          <ReviewAttrRow label={t('wizard.review.location')}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {data.repositoryLocation === 'local' ? (
                <Server size={12} style={{ opacity: 0.6 }} />
              ) : (
                <Cloud size={12} style={{ opacity: 0.6 }} />
              )}
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.75rem',
                }}
              >
                {executionTarget === 'agent'
                  ? t('wizard.review.repositoryPathOnSelectedAgent')
                  : data.repositoryLocation === 'local'
                    ? t('wizard.review.borgUiServer')
                    : isDirectRclone
                      ? t('wizard.review.directRclone')
                      : data.repositoryLocation === 'rclone'
                        ? t('wizard.review.rcloneStorage')
                        : t('wizard.review.sshRemote')}
              </Typography>
            </Box>
          </ReviewAttrRow>

          <ReviewAttrRow label={t('wizard.review.execution')}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {executionTarget === 'agent' ? (
                <Laptop size={12} style={{ opacity: 0.6 }} />
              ) : (
                <Server size={12} style={{ opacity: 0.6 }} />
              )}
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.75rem',
                }}
              >
                {executionTarget === 'agent'
                  ? t('wizard.review.managedAgent')
                  : t('wizard.review.borgUiServer')}
              </Typography>
            </Box>
          </ReviewAttrRow>

          {executionTarget === 'agent' && (
            <ReviewAttrRow label={t('wizard.review.agent')}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 500,
                }}
              >
                {selectedAgent?.hostname || selectedAgent?.name || t('wizard.review.notSet')}
              </Typography>
            </ReviewAttrRow>
          )}

          <ReviewAttrRow label={t('wizard.review.path')}>
            <ReviewCodePill>{data.path || t('wizard.review.notSet')}</ReviewCodePill>
          </ReviewAttrRow>

          {isDirectRclone && (
            <>
              <ReviewAttrRow label={t('wizard.review.rcloneRoute')}>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.75rem',
                  }}
                >
                  {t('wizard.location.directRcloneRoutePreview')}
                </Typography>
              </ReviewAttrRow>
              <ReviewAttrRow label={t('wizard.review.directRcloneTradeoffsLabel')}>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.75rem',
                  }}
                >
                  {t('wizard.review.directRcloneTradeoffs')}
                </Typography>
              </ReviewAttrRow>
              {data.rcloneSyncPolicy === 'scheduled' && (
                <ReviewAttrRow label={t('wizard.review.rcloneSyncSchedule')}>
                  <ReviewCodePill>
                    {`${data.rcloneSyncCronExpression || t('wizard.review.notSet')} · ${
                      data.rcloneSyncTimezone || 'UTC'
                    }`}
                  </ReviewCodePill>
                </ReviewAttrRow>
              )}
            </>
          )}
        </ReviewSectionCard>

        {data.cloudMirrorEnabled && (
          <ReviewSectionCard
            icon={<Cloud size={14} />}
            label={t('wizard.review.cloudMirror')}
            accentColor={theme.palette.secondary.main}
          >
            <ReviewAttrRow label={t('wizard.review.rcloneRoute')}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.75rem',
                }}
              >
                {t('wizard.cloudMirror.routePreview')}
              </Typography>
            </ReviewAttrRow>
            <ReviewAttrRow label={t('wizard.review.rcloneRemotePath')}>
              <ReviewCodePill>{cloudMirrorTarget}</ReviewCodePill>
            </ReviewAttrRow>
            <ReviewAttrRow label={t('wizard.review.rcloneSyncPolicy')}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.75rem',
                }}
              >
                {getRcloneSyncPolicyLabel()}
              </Typography>
            </ReviewAttrRow>
            {data.rcloneSyncPolicy === 'scheduled' && (
              <ReviewAttrRow label={t('wizard.review.rcloneSyncSchedule')}>
                <ReviewCodePill>
                  {`${data.rcloneSyncCronExpression || t('wizard.review.notSet')} · ${
                    data.rcloneSyncTimezone || 'UTC'
                  }`}
                </ReviewCodePill>
              </ReviewAttrRow>
            )}
          </ReviewSectionCard>
        )}

        {/* SECURITY */}
        <ReviewSectionCard
          icon={<Shield size={14} />}
          label={t('wizard.review.security')}
          accentColor={isEncrypted ? theme.palette.success.main : theme.palette.error.main}
        >
          {mode === 'create' && (
            <ReviewAttrRow label={t('wizard.review.encryption')}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                {isEncrypted ? (
                  <Lock size={11} color={theme.palette.success.main} />
                ) : (
                  <Unlock size={11} color={theme.palette.error.main} />
                )}
                <Chip
                  label={t(getEncryptionLabelKey(data.encryption))}
                  size="small"
                  color={isEncrypted ? 'success' : 'error'}
                  sx={{ height: 17, fontSize: '0.62rem', fontWeight: 600 }}
                />
              </Box>
            </ReviewAttrRow>
          )}

          <ReviewAttrRow label={t('wizard.review.passphrase')}>
            {data.passphrase ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: showPassphrase ? 'inherit' : 'monospace',
                    fontSize: '0.75rem',
                    letterSpacing: showPassphrase ? 'normal' : '0.1em',
                  }}
                >
                  {showPassphrase ? data.passphrase : '••••••••'}
                </Typography>
                <Tooltip
                  title={
                    showPassphrase
                      ? t('wizard.review.hidePassphrase')
                      : t('wizard.review.showPassphrase')
                  }
                >
                  <IconButton
                    aria-label={
                      showPassphrase
                        ? t('wizard.review.hidePassphrase')
                        : t('wizard.review.showPassphrase')
                    }
                    onClick={() => setShowPassphrase((v) => !v)}
                    size="small"
                    sx={{ p: 0.2 }}
                  >
                    {showPassphrase ? <EyeOff size={11} /> : <Eye size={11} />}
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                }}
              >
                {t('wizard.review.passphraseNotSet')}
              </Typography>
            )}
          </ReviewAttrRow>
        </ReviewSectionCard>

        {/* DATA SOURCE - full mode only */}
        {hasPlanOwnedAgentSources && (
          <ReviewSectionCard
            icon={<Info size={14} />}
            label={t('wizard.review.planOwnedSources')}
            accentColor={theme.palette.warning.main}
          >
            <ReviewAttrRow label={t('wizard.review.source')}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 500,
                }}
              >
                {t('wizard.review.backupPlans')}
              </Typography>
            </ReviewAttrRow>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: '0.75rem',
              }}
            >
              {t('wizard.review.planOwnedSourcesDetail')}
            </Typography>
          </ReviewSectionCard>
        )}

        {hasBackupSource && (
          <ReviewSectionCard
            icon={data.dataSource === 'local' ? <HardDrive size={14} /> : <Laptop size={14} />}
            label={t('wizard.review.dataSource')}
            accentColor={theme.palette.warning.main}
          >
            <ReviewAttrRow label={t('wizard.review.source')}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 500,
                }}
              >
                {executionTarget === 'agent'
                  ? t('wizard.review.managedAgent')
                  : data.dataSource === 'local'
                    ? t('wizard.review.borgUiServer')
                    : t('wizard.review.remoteClient')}
              </Typography>
            </ReviewAttrRow>

            {data.dataSource === 'local' && (
              <>
                <ReviewAttrRow label={t('wizard.review.directories')}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.75rem',
                    }}
                  >
                    {t('wizard.review.directoriesCount', { count: data.sourceDirs.length })}
                  </Typography>
                </ReviewAttrRow>

                <ReviewAttrRow label={t('wizard.review.excludePatterns')}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.75rem',
                    }}
                  >
                    {t('wizard.review.directoriesCount', { count: data.excludePatterns.length })}
                  </Typography>
                </ReviewAttrRow>
              </>
            )}
          </ReviewSectionCard>
        )}

        {/* BACKUP CONFIG - full mode only */}
        {hasBackupConfiguration && (
          <ReviewSectionCard
            icon={<Settings size={14} />}
            label={t('wizard.review.backupConfiguration')}
            accentColor={theme.palette.secondary.main}
          >
            <ReviewAttrRow label={t('wizard.review.compression')}>
              <ReviewCodePill>{data.compression}</ReviewCodePill>
            </ReviewAttrRow>

            {data.customFlags && (
              <ReviewAttrRow label={t('wizard.review.customFlags')}>
                <ReviewCodePill>{data.customFlags}</ReviewCodePill>
              </ReviewAttrRow>
            )}
          </ReviewSectionCard>
        )}
      </ReviewSectionGrid>

      {/* Import / edit notes */}
      {mode === 'import' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Info size={14} style={{ opacity: 0.45, flexShrink: 0 }} />
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {t('wizard.review.repositoryImportNote')}
          </Typography>
        </Box>
      )}

      {mode === 'edit' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Info size={14} style={{ opacity: 0.45, flexShrink: 0 }} />
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {t('wizard.review.repositoryEditNote')}
          </Typography>
        </Box>
      )}
    </Box>
  )
}
